
import { computed, ref } from "vue";

declare const faceapi: any;

export default {
    name: "UploadDocuments",
    props: ['baseUrl'],
    setup(props: any) {
        const loggedIn = ref(false);
        const documentsList = ref<string[]>([]);
        const iconUrls = ref<{ [key: string]: string }>({});
        const OfficerId = localStorage.OfficerId;
        
        const isProcessing = ref(false);
        const faceStatusMessage = ref('');
        const faceStatusClass = ref('');
        const savedFeatures = ref<number[] | null>(null);
        const faceapiLoaded = ref(false);
        
        // Track extraction loading state per document
        const isExtracting = ref<{ [key: string]: boolean }>({
            Passport: false,
            Photo: false,
            SIABadge: false
        });
        
        // Store file input references
        const fileInputs = ref<{ [key: string]: HTMLInputElement | null }>({});
        
        var token = localStorage.getItem("token")!;
        const baseUrlAddress = computed(() => `${props.baseUrl}`);

        // ============================================================
        // DOCUMENTS CONFIGURATION - Single source of truth for all rows
        // ============================================================
        const documentsConfig = [
            { key: 'Passport', displayName: 'Passport', isPdf: false, extractFace: true },
            { key: 'Photo', displayName: '35x45mm Photo', isPdf: false, extractFace: true },
            { key: 'Visa', displayName: 'Visa Share Code', isPdf: false, extractFace: false },
            { key: 'SIABadge', displayName: 'SIA Licence', isPdf: false, extractFace: true },
            { key: 'NINoDoc', displayName: 'NIN/UTR', isPdf: false, extractFace: false },
            { key: 'DrivingLicence', displayName: 'DrivingLicence', isPdf: false, extractFace: false },
            { key: 'AddressDoc', displayName: 'Proof of Address 01', isPdf: false, extractFace: false },
            { key: 'AdditionalDoc1', displayName: 'Proof of Address 02', isPdf: false, extractFace: false },
            { key: 'AdditionalDoc2', displayName: 'Supporting Document 01', isPdf: false, extractFace: false },
            { key: 'AdditionalDoc3', displayName: 'Supporting Document 02', isPdf: false, extractFace: false },
            { key: 'documents', displayName: 'All Documents PDF', isPdf: true, extractFace: false },
            { key: 'screenshot', displayName: 'ScreenShot Photo', isPdf: false },
            { key: 'interviewQuestionaire', displayName: 'Questionaire', isPdf: false },
        ];

        const getIconUrl = (documentName: string) => {
            return iconUrls.value[documentName] || null;
        };

        // The timestamp in the name sorts chronologically, so the last key is the newest check
        const latestRightToWorkKey = computed(() => {
            const keys = Object.keys(iconUrls.value).filter(k => k.startsWith('RightToWork_')).sort();
            return keys.length ? keys[keys.length - 1] : null;
        });

        // RightToWork_20260925_101530 -> 25/09/2026 10:15 (UTC, as saved by the API)
        const latestRightToWorkCheckedAt = computed(() => {
            const match = latestRightToWorkKey.value?.match(/^RightToWork_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})/);
            return match ? `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]} UTC` : '';
        });
        
        const setFileInputRef = (docName: string, el: any) => {
            if (el) {
                fileInputs.value[docName] = el as HTMLInputElement;
            }
        };
        
        const triggerFileInput = (docName: string) => {
            if (fileInputs.value[docName]) {
                fileInputs.value[docName]?.click();
            }
        };
        
        // Get the document config by key
        const getDocumentConfig = (documentName: string) => {
            return documentsConfig.find(doc => doc.key === documentName);
        };
        
        // ============================================================
        // NEW FUNCTION: Extract face from already uploaded document
        // ============================================================
        const extractFaceFromDocument = async (documentName: string) => {
            // Check if document exists
            if (!getIconUrl(documentName)) {
                faceStatusMessage.value = `❌ No ${documentName} image found. Please upload first.`;
                faceStatusClass.value = 'text-danger';
                setTimeout(() => { faceStatusMessage.value = ''; }, 3000);
                return;
            }
            
            // Set extracting state for this document
            isExtracting.value[documentName] = true;
            faceStatusMessage.value = `📥 Downloading ${documentName}...`;
            faceStatusClass.value = 'text-info';
            
            try {
                // Download the image from server
                const extension = '.jpg';
                const downloadUrl = props.baseUrl + 'SecurityOfficers/' + OfficerId + '/download/' + documentName + extension;
                
                const response = await fetch(downloadUrl, {
                    method: 'GET',
                    headers: {
                        "Authorization": "Bearer " + localStorage.token,
                    },
                });
                
                if (!response.ok) {
                    throw new Error(`Failed to download: ${response.status}`);
                }
                
                const blob = await response.blob();
                const file = new File([blob], `${documentName}.jpg`, { type: 'image/jpeg' });
                
                faceStatusMessage.value = `🔍 Extracting face from ${documentName}...`;
                
                // Extract features from the downloaded image
                const extractedFeatures = await extractFeaturesFromLocalFile(file);
                
                if (!extractedFeatures) {
                    faceStatusMessage.value = `😞 No face detected in ${documentName}. Please upload a clearer image.`;
                    faceStatusClass.value = 'text-danger';
                    return;
                }
                
                faceStatusMessage.value = `✅ Face detected! Saving features...`;
                
                // Save features to API
                const saved = await saveFacialFeaturesToApi(extractedFeatures);
                
                if (saved) {
                    savedFeatures.value = extractedFeatures;
                    faceStatusMessage.value = `🎉 Face extracted and saved from ${documentName}!`;
                    faceStatusClass.value = 'text-success';
                } else {
                    faceStatusMessage.value = `⚠️ Face detected but failed to save.`;
                    faceStatusClass.value = 'text-warning';
                }
                
            } catch (error) {
                console.error("Extraction error:", error);
                faceStatusMessage.value = `❌ Failed to extract face from ${documentName}. Please try again.`;
                faceStatusClass.value = 'text-danger';
            } finally {
                isExtracting.value[documentName] = false;
                setTimeout(() => { faceStatusMessage.value = ''; }, 4000);
            }
        };
        
        // ============================================================
        // UPDATED: Upload ONLY - no face extraction during upload
        // ============================================================
        const handleFileChange = async (documentName: string, event: Event) => {
            const target = event.target as HTMLInputElement;
            if (!target.files || !target.files[0]) return;
            
            const file = target.files[0];
            
            // Upload ONLY — no face extraction here anymore
            const formData = new FormData();
            formData.append('profile_pic', file);
            formData.append('documentName', documentName + '.jpg');
            
            try {
                faceStatusMessage.value = `📤 Uploading ${documentName}...`;
                faceStatusClass.value = 'text-info';
                
                const response = await fetch(`${props.baseUrl}SecurityOfficers/${OfficerId}/upload`, {
                    method: 'POST',
                    headers: {
                        "Authorization": "Bearer " + localStorage.token,
                    },
                    body: formData
                });
                
                await response.json();
                
                // Refresh document list and icons
                await checkDocumentsStatus();
                await loadIcons();
                
                // Reset file input
                if (fileInputs.value[documentName]) {
                    fileInputs.value[documentName]!.value = '';
                }
                
                faceStatusMessage.value = `✅ ${documentName} uploaded successfully! ${getDocumentConfig(documentName)?.extractFace ? 'Click "Extract" button above to process face.' : ''}`;
                faceStatusClass.value = 'text-success';
                
            } catch (error) {
                console.error("Upload error:", error);
                alert("Upload failed: " + (error as Error).message);
                faceStatusMessage.value = `❌ Upload failed for ${documentName}`;
                faceStatusClass.value = 'text-danger';
            } finally {
                setTimeout(() => { faceStatusMessage.value = ''; }, 3000);
            }
        };
        
        const handlePDFFileChange = async (documentName: string, event: Event) => {
            const target = event.target as HTMLInputElement;
            if (!target.files || !target.files[0]) return;
            
            const file = target.files[0];
            
            const formData = new FormData();
            formData.append('profile_pic', file);
            formData.append('documentName', documentName + '.pdf');
            formData.append('isPdf', 'true');
            
            try {
                const response = await fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/upload', {
                    method: 'POST',
                    headers: {
                        "Authorization": "Bearer " + localStorage.token,
                    },
                    body: formData
                });
                
                await response.json();
                await checkDocumentsStatus();
                await loadIcons();
                
                // Reset file input
                if (fileInputs.value[documentName]) {
                    fileInputs.value[documentName]!.value = '';
                }
                
                faceStatusMessage.value = `✅ ${documentName} uploaded successfully!`;
                faceStatusClass.value = 'text-success';
                setTimeout(() => { faceStatusMessage.value = ''; }, 3000);
                
            } catch (error) {
                console.error("Upload error:", error);
                alert("Upload failed: " + (error as Error).message);
            }
        };
        
        const extractFeaturesFromLocalFile = (file: File): Promise<number[] | null> => {
            return new Promise((resolve) => {
                (async () => {
                    try {
                        if (!faceapiLoaded.value) {
                            await loadFaceApiModels();
                        }
                        
                        const reader = new FileReader();
                        
                        reader.onload = async (e) => {
                            const img = new Image();
                            
                            img.onload = async () => {
                                try {
                                    let detections: any = null;
                                    
                                    try {
                                        detections = await faceapi
                                            .detectAllFaces(img)
                                            .withFaceLandmarks()
                                            .withFaceDescriptors();
                                    } catch (err) {
                                        const options = new faceapi.TinyFaceDetectorOptions({
                                            inputSize: 320,
                                            scoreThreshold: 0.5
                                        });
                                        detections = await faceapi
                                            .detectAllFaces(img, options)
                                            .withFaceLandmarks()
                                            .withFaceDescriptors();
                                    }
                                    
                                    if (!detections || detections.length === 0) {
                                        resolve(null);
                                        return;
                                    }
                                    
                                    if (detections.length > 1) {
                                        faceStatusMessage.value = '⚠️ Multiple faces detected. Use single face image.';
                                        resolve(null);
                                        return;
                                    }
                                    
                                    const descriptor = detections[0].descriptor;
                                    const features: number[] = Array.isArray(descriptor) ? 
                                        descriptor.map((value: any) => Number(value)) : 
                                        Object.values(descriptor).map((value: any) => Number(value));
                                    
                                    resolve(features);
                                    
                                } catch (error) {
                                    console.error('Face detection error:', error);
                                    resolve(null);
                                }
                            };
                            
                            img.onerror = () => resolve(null);
                            img.src = e.target?.result as string;
                        };
                        
                        reader.onerror = () => resolve(null);
                        reader.readAsDataURL(file);
                        
                    } catch (error) {
                        console.error('Error:', error);
                        resolve(null);
                    }
                })();
            });
        };
        
        const loadIcons = async () => {
            if (!OfficerId) {
                console.error("No OfficerId found");
                return;
            }
            
            try {
                const response = await fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/icons', {
                    method: 'GET',
                    headers: {
                        "Authorization": "Bearer " + localStorage.token,
                    },
                });
                
                const data = await response.json();
                const urlMap: { [key: string]: string } = {};
                
                if (Array.isArray(data)) {
                    data.forEach((url: string) => {
                        const match = url.match(/\/([^/?]+)\.(jpg|jpeg|png|gif|ico)/i);
                        if (match) {
                            const fileName = match[1];
                            urlMap[fileName] = url;
                        }
                    });
                }
                
                iconUrls.value = urlMap;
            } catch (err) {
                console.error(err);
            }
        };
        
        const checkDocumentsStatus = async () => {
            if (!OfficerId) return;
            
            try {
                const response = await fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/documents', {
                    method: 'GET',
                    headers: {
                        "Authorization": "Bearer " + localStorage.token,
                    },
                });
                const data = await response.json();
                documentsList.value = Array.isArray(data) ? data : [];
            } catch (error) {
                console.error(error);
            }
        };
        
        const downloadDocument = (fileName: string) => {
            let extension = '';
            let downloadUrl = '';
            
            if (fileName === 'documents') {
                extension = '.pdf';
                downloadUrl = props.baseUrl + 'SecurityOfficers/' + OfficerId + '/download/' + fileName + extension;
            } else {
                extension = '.jpg';
                downloadUrl = props.baseUrl + 'SecurityOfficers/' + OfficerId + '/download/' + fileName + extension;
            }
            
            fetch(downloadUrl, {
                method: 'GET',
                headers: {
                    "Authorization": "Bearer " + localStorage.token,
                },
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Download failed: ${response.status}`);
                }
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName + extension;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(error => {
                console.error("Download error:", error);
                alert("Failed to download file. Please try again.");
            });
        };
        
        const loadFaceApiModels = async () => {
            try {
                if (typeof faceapi === 'undefined') {
                    faceStatusMessage.value = 'Face detection library not loaded. Please refresh the page.';
                    faceStatusClass.value = 'text-danger';
                    console.error('face-api.js not loaded');
                    return false;
                }
                
                const MODEL_URL = '/models';
                
                await Promise.all([
                    faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
                    faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
                    faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
                    faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
                ]);
                
                faceapiLoaded.value = true;
                console.log('Face-api models loaded');
                return true;
            } catch (error) {
                console.error('Failed to load face-api models:', error);
                faceStatusMessage.value = 'Failed to load face detection models';
                faceStatusClass.value = 'text-danger';
                return false;
            }
        };
        
        const saveFacialFeaturesToApi = async (features: number[]): Promise<boolean> => {
            try {
                const response = await fetch(`${props.baseUrl}SecurityOfficers/${OfficerId}/SaveFacialFeature`, {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({ 
                        FacialFeatures: features
                    })
                });
                
                return response.ok;
            } catch (error) {
                console.error('API Error:', error);
                return false;
            }
        };
        
        //const token = localStorage.getItem("token");
        if (token == null) {
            loggedIn.value = false;
        } else {
            loggedIn.value = true;
            //checkDocumentsStatus();
            loadIcons();
        }
        
        return {
            loggedIn,
            documentsList,
            latestRightToWorkKey,
            latestRightToWorkCheckedAt,
            documentsConfig,
            getIconUrl,
            downloadDocument,
            isProcessing,
            faceStatusMessage,
            faceStatusClass,
            savedFeatures,
            isExtracting,
            setFileInputRef,
            triggerFileInput,
            handleFileChange,
            handlePDFFileChange,
            extractFaceFromDocument,  // <-- EXPOSED for buttons
        };
    }
};
